import React, { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import MessageIcon from "@mui/icons-material/Message";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import "./ContactPage.css";

interface ContactFormData {
  name: string;
  email: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    message: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const { publicApi } = await import("../services/api");
      await publicApi.contact(formData.name, formData.email, formData.message);
      setIsSubmitted(true);
    } catch (err) {
      setErrors({
        message:
          err instanceof Error ? err.message : "Failed to send message. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <div className="contact-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
      </div>

      <div className="contact-container">
        {/* Header */}
        <header className="contact-header">
          <Link to="/login" className="contact-logo-link">
            <div className="contact-logo">
              <img src="/clusterview-logo.svg" alt="ClusterView" className="logo-icon" style={{ width: 28, height: 28 }} />
            </div>
            <span>ClusterView</span>
          </Link>
        </header>

        {/* Hero */}
        <section className="contact-hero">
          <h1>Get in Touch</h1>
          <p>
            Got a question, query, or interested in our Enterprise plan?
            <br />
            Fill in the form below and we will get back to you.
          </p>
        </section>

        {/* Content */}
        <div className="contact-content">
          {/* Phone Info */}
          <div className="contact-phone-card">
            <PhoneIcon className="phone-icon" />
            <a href="tel:+17742346476" className="phone-number">
              +1 (774) 234-6476
            </a>
          </div>

          {/* Form or Success */}
          {isSubmitted ? (
            <div className="contact-success-card">
              <CheckCircleIcon className="success-icon" />
              <h2>Message Sent</h2>
              <p>
                Thank you for reaching out. Our team will get back to you
                shortly.
              </p>
              <div className="success-actions">
                <Link to="/pricing" className="success-btn">
                  Back to Pricing
                </Link>
                <button
                  className="success-btn outline"
                  onClick={() => {
                    setIsSubmitted(false);
                    setFormData({ name: "", email: "", message: "" });
                  }}
                >
                  Send Another Message
                </button>
              </div>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit}>
              {/* Name */}
              <div className="contact-form-group">
                <label htmlFor="contact-name">Your Name</label>
                <div className={`contact-input-wrapper ${errors.name ? "error" : ""}`}>
                  <PersonIcon className="input-icon" />
                  <input
                    id="contact-name"
                    type="text"
                    placeholder="Enter your name here"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, name: e.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </div>
                {errors.name && <span className="field-error">{errors.name}</span>}
              </div>

              {/* Email */}
              <div className="contact-form-group">
                <label htmlFor="contact-email">Email Address</label>
                <div className={`contact-input-wrapper ${errors.email ? "error" : ""}`}>
                  <EmailIcon className="input-icon" />
                  <input
                    id="contact-email"
                    type="email"
                    placeholder="Enter your email here"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, email: e.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </div>
                {errors.email && <span className="field-error">{errors.email}</span>}
              </div>

              {/* Message */}
              <div className="contact-form-group">
                <label htmlFor="contact-message">Message</label>
                <div className={`contact-input-wrapper textarea ${errors.message ? "error" : ""}`}>
                  <MessageIcon className="input-icon textarea-icon" />
                  <textarea
                    id="contact-message"
                    placeholder="Type here..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, message: e.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </div>
                {errors.message && (
                  <span className="field-error">{errors.message}</span>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="contact-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="contact-spinner"></span>
                    Sending...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <footer className="contact-footer">
          <p>
            <Link to="/pricing">View Pricing</Link>
            {" · "}
            <Link to="/login">Sign In</Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default ContactPage;
